import { useContext } from "react";
import { AuthContext } from "../../auth-context";
import {
  bmi,
  getAgeAtThatTime,
  getBMISd,
  getHeightSd,
  getWeightSd,
  getWHSd,
  wh
} from "../../utils/calculations";
import { yearsToYM } from "../../utils/date";
import { roundTo, tryOrMessage } from "../../utils/strings";
import IODate from "../../widget/io-date";
import IOList from "../../widget/io-list";
import IONumber from "../../widget/io-number";
import IOString from "../../widget/io-string";
import Panel from "../../widget/panel";
import TwoColumns from "../../widget/two-columns";
import type { Consult, Patient } from "../_objects-patient";

export default function ConsultAbstractIntroduction(props: {
  file: Consult;
  patient: Patient;
}): React.ReactNode {
  const authenticationContext = useContext(AuthContext)!;

  return (
    <TwoColumns>
      <Panel fixed label="Information">
        <input
          type="hidden"
          name="patient_id"
          defaultValue={props.patient.id}
        />
        <IODate name="date" value={props.file.date} required />
        <IOList
          name="examiner"
          value={props.file.examiner}
          list={authenticationContext.getList("Examiners")}
          required
        />
        <IOList
          name="center"
          value={props.file.center}
          list={authenticationContext.getList("Centers")}
          required
        />
        <IOString
          label="Age at consultation time"
          value={tryOrMessage(() =>
            yearsToYM(getAgeAtThatTime(props.file, props.patient))
          )}
        />
      </Panel>
      <Panel fixed label="Nutritional data">
        <IONumber
          name="weight_kg"
          label="Weight (kg)"
          value={props.file.weight_kg}
          htmlProps={{
            min: 3,
            max: 150
          }}
        />
        {props.file.weight_kg && (
          <IOString
            label="Weight sd"
            value={tryOrMessage(() =>
              roundTo(getWeightSd(props.file, props.patient))
            )}
          />
        )}
        <IONumber
          name="height_cm"
          label="Height (cm)"
          value={props.file.height_cm}
          htmlProps={{
            min: 30,
            max: 200
          }}
        />
        {props.file.height_cm && (
          <IOString
            label="Height sd"
            value={tryOrMessage(() =>
              roundTo(getHeightSd(props.file, props.patient))
            )}
          />
        )}
        <IONumber
          name="brachial_circumference_cm"
          label="Brachial Circumference (cm)"
          value={props.file.brachial_circumference_cm}
          htmlProps={{
            min: 6,
            max: 26
          }}
        />
        {props.file.weight_kg && props.file.height_cm && (
          <>
            <IOString
              label="Weight/Height ratio"
              value={tryOrMessage(() => roundTo(wh(props.file)))}
            />
            <IOString
              label="Weight/Height sd"
              value={tryOrMessage(
                () => roundTo(getWHSd(props.file, props.patient)),
                "?"
              )}
            />
            <IOString
              label="BMI"
              value={tryOrMessage(() => roundTo(bmi(props.file)))}
            />
            <IOString
              label="BMI sd"
              value={tryOrMessage(() =>
                roundTo(getBMISd(props.file, props.patient))
              )}
            />
          </>
        )}
      </Panel>
    </TwoColumns>
  );
}
